if (!customElements.get('karigari-arrivals-slider')) {
  customElements.define(
    'karigari-arrivals-slider',
    class KarigariArrivalsSlider extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('[id^="Slider-"]');
        if (this.slider) {
          this.slider.scrollLeft = 0;
        }

        this.cartQuantities = {};
        this.onPlusClick = this.handlePlusClick.bind(this);
        this.onCartUpdate = this.handleCartUpdate.bind(this);
        this.onResize = this.syncArrowPosition.bind(this);

        this.addEventListener('click', this.onPlusClick);
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
        window.addEventListener('resize', this.onResize);

        this.syncArrowPosition();
        requestAnimationFrame(() => this.syncArrowPosition());
        this.refreshCartState();

        if (!this.slider || this.dataset.autoplay !== 'true') return;

        this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this.reducedMotion) return;

        this.onMouseEnter = this.pauseAutoplay.bind(this);
        this.onMouseLeave = this.startAutoplay.bind(this);
        this.onFocusIn = this.pauseAutoplay.bind(this);
        this.onFocusOut = this.startAutoplay.bind(this);

        this.addEventListener('mouseenter', this.onMouseEnter);
        this.addEventListener('mouseleave', this.onMouseLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);

        this.startAutoplay();
      }

      disconnectedCallback() {
        this.pauseAutoplay();
        this.removeEventListener('click', this.onPlusClick);
        this.removeEventListener('mouseenter', this.onMouseEnter);
        this.removeEventListener('mouseleave', this.onMouseLeave);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
        window.removeEventListener('resize', this.onResize);
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      syncArrowPosition() {
        const arrows = this.querySelector('.karigari-new-arrivals__arrows');
        const media = this.querySelector('.card__inner');
        if (!arrows || !media) return;

        const top = media.offsetTop + media.offsetHeight / 2;
        arrows.style.top = `${top}px`;
        arrows.style.transform = 'translateY(-50%)';
      }

      handlePlusClick(event) {
        const plus = event.target.closest('.karigari-quick-add-plus');
        const inCartSubmit = event.target.closest('.quick-add__submit.is-in-cart');

        if (inCartSubmit && this.contains(inCartSubmit)) {
          event.preventDefault();
          event.stopPropagation();
          this.openCart(inCartSubmit);
          return;
        }

        if (!plus || !this.contains(plus)) return;

        // Quantity badge: open cart instead of adding again
        if (plus.classList.contains('is-in-cart')) {
          event.preventDefault();
          event.stopPropagation();
          this.openCart(plus);
          return;
        }

        // Native form-associated submit buttons work on their own
        if (plus.matches('[type="submit"][form]')) return;

        // Multi-variant / modal path
        if (!plus.hasAttribute('data-karigari-quick-plus')) return;

        event.preventDefault();
        event.stopPropagation();

        const card = plus.closest('.card-wrapper, .product-card-wrapper');
        if (!card) return;

        const submit = card.querySelector('.quick-add__submit');
        if (!submit || submit.disabled) return;

        const quickAdd = card.querySelector('.quick-add');
        if (quickAdd) {
          quickAdd.style.pointerEvents = 'auto';
          quickAdd.style.opacity = '1';
        }

        submit.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      }

      openCart(triggeredBy) {
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (cart && typeof cart.open === 'function') {
          cart.open(triggeredBy);
        } else if (window.routes && window.routes.cart_url) {
          window.location = window.routes.cart_url;
        }
      }

      handleCartUpdate() {
        this.refreshCartState();
      }

      refreshCartState() {
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        fetch(`${cartUrl}.js`, { credentials: 'same-origin' })
          .then((response) => response.json())
          .then((cart) => {
            this.cartQuantities = {};
            (cart.items || []).forEach((item) => {
              const id = String(item.variant_id);
              this.cartQuantities[id] = (this.cartQuantities[id] || 0) + item.quantity;
            });
            this.applyCartState();
          })
          .catch(() => {});
      }

      applyCartState() {
        this.querySelectorAll('.card-wrapper, .product-card-wrapper').forEach((card) => {
          const variantInput = card.querySelector('input.product-variant-id, [name="id"]');
          const variantId = variantInput && variantInput.value ? String(variantInput.value) : null;
          const quantity = variantId ? this.cartQuantities[variantId] || 0 : 0;

          const plus = card.querySelector('.karigari-quick-add-plus');
          const submit = card.querySelector('.quick-add__submit');
          const label = submit && submit.querySelector('span:not(.sold-out-message):not(.loading__spinner):not(.icon-wrap)');

          if (plus) {
            const plusLabel = plus.querySelector('span') || plus;
            if (quantity > 0) {
              plus.classList.add('is-in-cart');
              plusLabel.textContent = String(quantity);
              plus.setAttribute('aria-label', `${quantity} in cart`);
              if (plus.matches('[type="submit"]')) {
                plus.dataset.originalType = 'submit';
                plus.type = 'button';
              }
            } else {
              plus.classList.remove('is-in-cart');
              plusLabel.textContent = '+';
              plus.setAttribute('aria-label', 'Add to cart');
              if (plus.dataset.originalType === 'submit') {
                plus.type = 'submit';
              }
            }
          }

          if (submit && label && !submit.querySelector('.sold-out-message:not(.hidden)')) {
            if (!submit.dataset.originalLabel) {
              submit.dataset.originalLabel = label.textContent.trim();
            }
            if (quantity > 0) {
              submit.classList.add('is-in-cart');
              label.textContent = 'Item already in cart';
            } else {
              submit.classList.remove('is-in-cart');
              label.textContent = submit.dataset.originalLabel || 'Add to cart';
            }
          }
        });

        this.syncArrowPosition();
      }

      startAutoplay() {
        this.pauseAutoplay();
        this.autoplayTimer = window.setInterval(() => this.advance(), this.speed);
      }

      pauseAutoplay() {
        if (this.autoplayTimer) {
          window.clearInterval(this.autoplayTimer);
          this.autoplayTimer = null;
        }
      }

      advance() {
        const slides = Array.from(this.slider.querySelectorAll('.slider__slide')).filter(
          (slide) => slide.clientWidth > 0
        );
        if (slides.length < 2) return;

        const slideOffset = slides[1].offsetLeft - slides[0].offsetLeft;
        const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
        let nextPosition = this.slider.scrollLeft + slideOffset;

        if (nextPosition >= maxScroll - 2) {
          nextPosition = 0;
        }

        this.slider.scrollTo({
          left: nextPosition,
          behavior: 'smooth',
        });
      }
    }
  );
}
